import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight, ShieldCheck, Zap, Globe, Database, Sparkles } from "lucide-react";
import LiveCounters from "../components/LiveCounters";
import InlineSnapshotWidget from "../components/InlineSnapshotWidget";
import CaseStudyLink from "../components/CaseStudyLink";
import { trackEvent } from "../lib/analytics";

/**
 * V232.6 — Comparison SEO page: TopRankPulse vs Semrush.
 *
 * Semrush is the global SEO heavyweight (~€220/mes Guru plan, used by
 * agencies in 100+ countries). Brand searches on Google.sk + .cz are
 * massive but conversion intent is lower — most searchers come from
 * agency staff benchmarking plans, not buyers switching. The angle
 * is DELIBERATELY segmentation-based: "Semrush for US/EU markets,
 * TopRankPulse for SK/CZ with AI layer".
 *
 * Who this page is really for:
 *   • Slovak/Czech expats + remote workers Googling "semrush
 *     alternative slovakia"
 *   • Agencies evaluating "cheaper Semrush for small SK clients"
 *   • Startup founders who saw Semrush ad and want local option
 *
 * We openly acknowledge Semrush dominates most classical SEO features
 * (10× bigger keyword DB, 2× bigger backlink DB). The win is in
 * (1) price for SK/CZ-only use case, (2) AI citation tracking,
 * (3) local firm database (ORSF + ARES) that Semrush doesn't have.
 */
export default function CompareSemrushPage() {
  const [faqOpen, setFaqOpen] = useState(null);
  const canonical = "https://toprankpulse.com/compare/toprankpulse-vs-semrush";

  useEffect(() => {
    trackEvent("view_compare_semrush", {
      competitor: "semrush",
      page_type: "versus",
    });
  }, []);

  const faqs = [
    {
      q: "Je TopRankPulse alternatíva k Semrushu pre slovenské firmy?",
      a: "Pre špecifický use case SK/CZ trh + AI citation tracking áno. Semrush dominuje v globálnom klasickom SEO (keyword research, SERP, backlinky), kde má 10× väčšiu databázu. TopRankPulse je 5× lacnejší pre SK/CZ-only projekt a navyše ponúka AI citation tracking v 6 modeloch, čo Semrush k 02/2026 nemá.",
    },
    {
      q: "Koľko stojí Semrush a koľko TopRankPulse?",
      a: "Semrush Pro plán od €117/mes, Guru plán €220/mes, Business plán €450/mes. Všetky plány majú limitovaný počet projektov a kľúčových slov. TopRankPulse Pro: €49/mes neobmedzene SK/CZ domén. Pre lokálny SK/CZ biznis to je rozdiel 1 026 € za 6 mesiacov v prospech TopRankPulse (Guru vs Pro).",
    },
    {
      q: "Pre koho je Semrush lepší?",
      a: "Pre agentúry pracujúce s medzinárodnými klientmi (US, EÚ-26, LATAM), pre e-commerce ktoré potrebujú detailné backlink audity, pre PPC tímy s rozpočtami v tisícoch euro mesačne. Semrush má najkomplexnejší global SEO stack na trhu.",
    },
    {
      q: "Pre koho je TopRankPulse lepší?",
      a: "Pre SK a CZ firmy ktoré chcú (1) AI citation tracking v ChatGPT, Perplexity, Gemini — kde ich Semrush neukáže vôbec; (2) ušetriť 70-85% nákladov oproti Semrush pre lokálny biznis; (3) programmatic SEO pre každú SK/CZ firmu podľa IČO (ORSF + ARES integrácie); (4) SK UI a SK podporu.",
    },
    {
      q: "Môžem Semrush a TopRankPulse používať spoločne?",
      a: "Áno — mnoho našich klientov s globálnymi klientami má Semrush pre US/UK/DE audity a TopRankPulse pre SK/CZ projekty + AI citation layer naprieč všetkými. API-first prístup oboch nástrojov umožňuje spoločný export do dashboardov ako Looker Studio.",
    },
    {
      q: "Má Semrush AI SEO funkcie?",
      a: "Semrush začal v 2024-2025 pridávať niektoré AI-themed features (napr. 'SEO AI Toolkit'), ale k 02/2026 nemá dedikované meranie koľkokrát ťa citujú ChatGPT, Perplexity alebo Gemini. TopRankPulse je od začiatku AI-first — meranie citácií v 6 modeloch je naša core funkcia.",
    },
  ];

  const comparisonRows = [
    { feature: "Cena entry plan", tp: "€7 jednorazový / €49/mes neobmedzene", sr: "€117/mes Pro (limit)", winner: "tp" },
    { feature: "Cena Standard plan", tp: "€49/mes neobmedzene SK/CZ domén", sr: "€220/mes Guru", winner: "tp" },
    { feature: "AI citation tracking (ChatGPT/Perplexity/Gemini)", tp: true, sr: false, winner: "tp" },
    { feature: "FAQPage + HowTo schema (AEO)", tp: true, sr: "čiastočne (manuálne)", winner: "tp" },
    { feature: "llms.txt detekcia", tp: true, sr: false, winner: "tp" },
    { feature: "Keyword databáza (globálna)", tp: "~5M SK/CZ fráz", sr: "25+ miliárd global fráz", winner: "sr" },
    { feature: "Keyword databáza SK + CZ", tp: "~5M fráz", sr: "100M+ (limit v nižších planoch)", winner: "sr" },
    { feature: "Backlink databáza", tp: "základná", sr: "43 biliónov backlink DB", winner: "sr" },
    { feature: "SERP tracker (desktop + mobile + lokalizovaný)", tp: "SK/CZ pokrytie", sr: "190+ krajín", winner: "sr" },
    { feature: "Historical SERP data", tp: "6 mesiacov", sr: "roky (až 2012)", winner: "sr" },
    { feature: "Programmatic SEO landing pages", tp: "153 SK/CZ miest + 1000+ firiem", sr: false, winner: "tp" },
    { feature: "Živá SK/CZ firemná databáza (ORSF + ARES)", tp: true, sr: false, winner: "tp" },
    { feature: "Mesačný AI audit (self-serve, 10 min)", tp: true, sr: false, winner: "tp" },
    { feature: "SK UI + SK podpora", tp: "plne SK", sr: "iba EN", winner: "tp" },
    { feature: "GDPR + EU-hosting", tp: true, sr: "US-based (DPA dostupná)", winner: "tp" },
    { feature: "API prístup", tp: true, sr: true, winner: "tie" },
    { feature: "Agency / White-label", tp: "€149/mes", sr: "iba Business plán €450/mes", winner: "tp" },
    { feature: "Content marketing suite (writing assistant, topic research)", tp: "základný", sr: "pokročilý (SEO Writing Assistant)", winner: "sr" },
    { feature: "Záruka vrátenia peňazí", tp: "14 dní", sr: "7 dní (free trial)", winner: "tp" },
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "TopRankPulse AI SEO Audit",
    description:
      "AI-first SEO audit platforma pre SK + CZ firmy. Reálne odhady ChatGPT, Perplexity a Gemini citácií za €7, mesačný AI monitoring za €49.",
    brand: { "@type": "Brand", name: "TopRankPulse" },
    offers: {
      "@type": "Offer",
      price: "7.00",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: "https://toprankpulse.com/snapshot",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "3874",
    },
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>TopRankPulse vs Semrush — Lokálna AI SEO alebo globálny giant? 2026</title>
        <meta
          name="description"
          content="TopRankPulse vs Semrush: AI citation tracking + SK/CZ fokus za €49/mes vs globálny SEO giant za €220/mes. Objektívne porovnanie 19 funkcií pre lokálne firmy a expats."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="TopRankPulse vs Semrush — Lokálna AI SEO alebo globálny giant?" />
        <meta property="og:description" content="AI citation tracking + SK/CZ fokus za €49/mes vs globálny SEO giant za €220/mes. Porovnanie 19 funkcií." />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="sk_SK" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <section className="px-6 pt-16 pb-8 max-w-4xl mx-auto">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#FFE600] mb-4 font-['JetBrains_Mono']">
          <span>Porovnanie 2026</span>
          <span className="opacity-50">·</span>
          <span className="text-gray-400">Aktualizované {new Date().toLocaleDateString("sk-SK")}</span>
        </div>

        <h1
          data-testid="compare-h1"
          className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] leading-tight mb-5 tracking-tight"
        >
          TopRankPulse <span className="text-gray-500">vs</span>{" "}
          <span className="text-[#ff642c]">Semrush</span>
          <br />
          <span className="text-2xl sm:text-3xl text-gray-300 font-bold">
            Lokálna AI SEO za €49, alebo globálny giant za €220/mes?
          </span>
        </h1>

        <p className="text-lg text-gray-300 mb-6 max-w-2xl leading-relaxed" data-testid="compare-tldr">
          <strong className="text-white">TL;DR:</strong> Semrush je globálny líder (US firma, 25+ miliárd keywords,
          43 biliónov backlinkov) za <strong className="text-white">€220/mes Guru plán</strong>.
          TopRankPulse je <strong className="text-[#a855f7]">EU-native AI-first platforma</strong>{" "}
          zameraná na SK/CZ + AI citation tracking za <strong className="text-[#a855f7]">€49/mes</strong>.
          Z 19 porovnaných funkcií vyhráva TopRankPulse 12, Semrush 6, remíza 1. Semrush pre US/EU trhy, TopRankPulse pre SK/CZ s AI vrstvou.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="compare-cta-row">
          <Link
            to="/snapshot"
            onClick={() => trackEvent("compare_cta_click", { destination: "/snapshot", competitor: "semrush" })}
            className="inline-flex items-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-white font-black px-6 py-3 rounded-lg transition-all"
            data-testid="compare-cta-primary"
          >
            Skúsiť TopRankPulse za €7 <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href="#comparison-table"
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold px-6 py-3 rounded-lg transition-all"
            data-testid="compare-cta-secondary"
          >
            Prejsť na porovnanie
          </a>
        </div>

        <LiveCounters variant="dark" />

        <div className="mt-6" data-testid="compare-inline-widget-wrap">
          <InlineSnapshotWidget
            source="compare_semrush_hero_widget"
            variant="dark"
            headline={
              <>
                Zisti sám — <span className="text-[#a855f7]">za €7, nie €220/mes</span>
              </>
            }
            subcopy="Semrush report ťa stojí mesačný retainer. TopRankPulse AI Snapshot ti dá SK/CZ-špecifický audit za 10 minút. 14-dňová záruka vrátenia peňazí."
            ctaLabel="Spustiť môj audit za €7"
          />
        </div>
      </section>

      {/* QUICK VERDICT CARDS */}
      <section className="px-6 py-8 max-w-4xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="compare-verdict-cards">
          <VerdictCard
            testid="verdict-tp"
            variant="win"
            brand="TopRankPulse"
            price="od €7 / jednorazový · €49/mes neobmedzene"
            best="Pre SK/CZ firmy, expati, remote workers a lokálne agentúry s AI SEO fókusom"
            points={[
              "AI citation tracking v 6 modeloch",
              "SK/CZ firemná databáza (ORSF + ARES)",
              "10-minútový self-serve audit",
              "SK UI · SK podpora · GDPR + EU hosting",
              "14-dňová záruka vrátenia peňazí",
            ]}
            accent="#a855f7"
          />
          <VerdictCard
            testid="verdict-sr"
            variant="neutral"
            brand="Semrush"
            price="€117-450/mes podľa plánu"
            best="Pre agentúry s globálnymi klientami, e-commerce s pokročilými backlink potrebami"
            points={[
              "25+ miliárd global keyword databáza",
              "43 biliónov backlink DB",
              "Historická SERP dáta od roku 2012",
              "SERP tracking v 190+ krajinách",
              "SEO Writing Assistant & Content toolkit",
            ]}
            accent="#ff642c"
          />
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section id="comparison-table" className="px-6 py-12 max-w-4xl mx-auto">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6" data-testid="compare-h2-table">
          Porovnanie 19 funkcií
        </h2>
        <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/5">
          <table className="w-full text-sm" data-testid="compare-table">
            <thead className="bg-black/40">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-gray-300">Funkcia</th>
                <th className="text-left px-4 py-3 font-bold text-[#a855f7]">TopRankPulse</th>
                <th className="text-left px-4 py-3 font-bold text-[#ff642c]">Semrush</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, i) => (
                <tr key={row.feature} className={i % 2 ? "bg-white/[0.02]" : ""} data-testid={`compare-row-${i}`}>
                  <td className="px-4 py-3 text-gray-200">{row.feature}</td>
                  <td className={`px-4 py-3 font-semibold ${row.winner === "tp" ? "text-emerald-400" : "text-gray-200"}`}>
                    <Cell value={row.tp} highlight={row.winner === "tp"} />
                  </td>
                  <td className={`px-4 py-3 ${row.winner === "sr" ? "text-emerald-400 font-semibold" : "text-gray-400"}`}>
                    <Cell value={row.sr} highlight={row.winner === "sr"} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-black/40 border-t border-white/10">
              <tr>
                <td className="px-4 py-3 font-bold text-gray-200">Celkové skóre</td>
                <td className="px-4 py-3 font-black text-emerald-400">12 výhier · 1 remíza</td>
                <td className="px-4 py-3 text-gray-300">6 výhier · 1 remíza · 12 strát</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="text-xs text-gray-500 mt-3" data-testid="compare-disclaimer">
          Zdroj cien: verejne dostupný cenník Semrush k 02/2026 (semrush.com/prices). Porovnanie
          prebieha v súlade s EU Directive 2006/114/EC — všetky tvrdenia overiteľné z verejných
          zdrojov. Pozn.: Semrush dominuje v klasickom SEO pre globálne trhy; TopRankPulse je
          lepší pre SK/CZ-only use case + AI SEO vrstvu.
        </p>
      </section>

      {/* CASE STUDY CROSS-LINK */}
      <CaseStudyLink competitorKey="semrush" />

      {/* WHO FOR / COMPLEMENT */}
      <section className="px-6 py-12 max-w-4xl mx-auto" data-testid="compare-who-for">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6">
          Kedy voliť ktorý nástroj?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReasonCard
            icon={Globe}
            title="Globálny biznis → Semrush"
            body="Ak máš klientov v US, UK, DE a tvoj revenue z lokálneho SK/CZ trhu je pod 30%, Semrush má lepšie pokrytie. Jeho keyword a backlink databázy sú rádovo väčšie."
          />
          <ReasonCard
            icon={Sparkles}
            title="SK/CZ fokus + AI → TopRankPulse"
            body="Ak 70%+ tvojho biznisu je SK/CZ, platíš za Semrush funkcionalitu ktorú nepotrebuješ. TopRankPulse ti dodá to isté lokálne + AI citation tracking za zlomok ceny."
          />
          <ReasonCard
            icon={Database}
            title="Expati + remote workers"
            body="Žiješ v SK/CZ, pracuješ s globálnymi klientami? Používaj oba. Semrush na globál, TopRankPulse na AI SEO vrstvu + SK/CZ projekty. Spoločný náklad stále pod €260/mes."
          />
          <ReasonCard
            icon={Zap}
            title="Quick audit → TopRankPulse"
            body="Chceš 10-minútový audit konkrétnej domény bez mesačnej subscription? Semrush ti to nedá — TopRankPulse áno za €7 jednorazovo."
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 py-12 max-w-4xl mx-auto" data-testid="compare-faq">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6">Časté otázky</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={f.q} className="rounded-xl border border-white/10 bg-white/5 overflow-hidden" data-testid={`compare-faq-${i}`}>
              <button
                type="button"
                onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-white/5"
                data-testid={`compare-faq-toggle-${i}`}
              >
                <span className="font-bold text-base">{f.q}</span>
                <span className={`text-gray-400 transition-transform ${faqOpen === i ? "rotate-45" : ""}`}>+</span>
              </button>
              {faqOpen === i && (
                <div className="px-5 pb-5 text-sm text-gray-300 leading-relaxed" data-testid={`compare-faq-answer-${i}`}>
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 py-16 max-w-4xl mx-auto" data-testid="compare-final-cta">
        <div className="rounded-2xl border border-[#a855f7]/40 bg-gradient-to-br from-[#a855f7]/20 via-[#a855f7]/5 to-transparent p-8 sm:p-10">
          <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-[#FFE600] mb-3 font-['JetBrains_Mono']">
            <ShieldCheck className="w-4 h-4" />
            <span>14-dňová záruka vrátenia peňazí</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-black font-['Cabinet_Grotesk'] mb-3 leading-tight">
            Semrush ťa stojí mesačný retainer. My ťa stojíme kávu.
          </h2>
          <p className="text-gray-300 mb-6 max-w-xl">
            Za <strong className="text-white">€7</strong> ti za 10 minút ukážeme SK/CZ-špecifický AI
            SEO audit + reálne odhady citácií v ChatGPT, Perplexity, Gemini. 14-dňová záruka.
          </p>
          <Link
            to="/snapshot"
            onClick={() => trackEvent("compare_final_cta_click", { destination: "/snapshot", competitor: "semrush" })}
            className="inline-flex items-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-white font-black px-7 py-4 rounded-lg text-lg transition-all"
            data-testid="compare-final-cta-btn"
          >
            Spustiť audit za €7 <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-[11px] text-gray-500 mt-4" data-testid="compare-final-cta-trust">
            🇸🇰 Prevádzkuje RESTOB s.r.o., Bratislava · GDPR-compliant · dáta neopúšťajú EU
          </p>
        </div>
      </section>
    </div>
  );
}

function Cell({ value, highlight }) {
  if (value === true) {
    return (
      <span className={`inline-flex items-center gap-1 ${highlight ? "text-emerald-400" : "text-emerald-500"}`}>
        <Check className="w-4 h-4" /> Áno
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1 text-gray-500">
        <X className="w-4 h-4" /> Nie
      </span>
    );
  }
  return <span>{value}</span>;
}

function VerdictCard({ brand, price, best, points, variant, testid, accent }) {
  const isWin = variant === "win";
  const borderCls = isWin ? "border-[#a855f7]/40" : "border-white/10";
  const bgCls = isWin ? "bg-gradient-to-br from-[#a855f7]/15 to-transparent" : "bg-white/5";
  return (
    <div className={`rounded-xl border ${borderCls} ${bgCls} p-6`} data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-xl font-black font-['Cabinet_Grotesk']" style={accent ? { color: accent } : undefined}>{brand}</h3>
        {isWin && (
          <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
            Odporúčané pre SK/CZ
          </span>
        )}
      </div>
      <p className="text-sm text-gray-300 mb-1">{price}</p>
      <p className="text-xs text-gray-400 mb-4 leading-relaxed">{best}</p>
      <ul className="space-y-2">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-2 text-sm text-gray-200">
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0`} style={{ color: accent || "#6b7280" }} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReasonCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-5">
      <Icon className="w-5 h-5 text-[#FFE600] mb-3" />
      <h3 className="font-bold text-base mb-1">{title}</h3>
      <p className="text-sm text-gray-400 leading-relaxed">{body}</p>
    </div>
  );
}
